import { useDebouncedCallback, useDebouncedValue } from '@mantine/hooks';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormControlLabelProps,
  LinearProgress,
  useTheme,
} from '@mui/material';
import {
  DataGridPro,
  DataGridProProps,
  GridCallbackDetails,
  GridCell,
  GridCellProps,
  GridColDef,
  GridRow,
  GridRowProps,
  GridSlotProps,
  GridSlots,
  GridRowSelectionModel,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import classNames from 'classnames';
import SkeletonBox from 'components/generic/SkeletonBox';
import { EditorialMiniMenu } from 'components/Minimenus/EditorialMiniMenu';
import { TMenuState, TSetMenuEvent } from 'components/Minimenus/MiniMenuSelector';
import {
  SearchRowMenuContext,
  SearchRowMenuContextValue,
} from 'components/Search/components/SearchRowActionsCell';
import { SearchGridCellProps } from 'components/Search/components/SearchGridCell';
import { getGridColumnDefs, getGridInitialState } from 'components/Search/gridColumnDefs';
import { getDatagridStylesInfo } from 'components/Search/styles';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { useCosmosEditorial } from 'providers/contexts/CosmosEditorialContext';
import {
  forwardRef,
  Fragment,
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useInfinitePaginationContext, useListContext } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { isDeepEqual } from 'remeda';
import { EditorialAssetSearchResultItem } from 'utility/types/dataProvider';
import { useSearchModalContext } from '../SearchModal';
import { defaultColumnsManagementSlotProps } from 'utility/dataGridDefaults';

import {
  augmentGridState,
  buildColDefsViaStateExport,
  checkSelectDisabled,
  getGridStorageLSKey,
  saveGridStateLocalStorage,
} from '../utilities';

/**
 * Types to declutter component code
 */

// Type is same as GridColDef<EditorialAssetSearchResultItem>[]
type EditorialCols = GridColDef<EditorialAssetSearchResultItem>;
type EditorialColDefs = EditorialCols[];
type ReactGridInitState =
  | DataGridProProps<EditorialAssetSearchResultItem>['initialState']
  | null;
type ReactRowsInitState = EditorialAssetSearchResultItem[] | undefined;

type GetRowClassnameFn = NonNullable<
  DataGridProProps<EditorialAssetSearchResultItem>['getRowClassName']
>;
type GetRowSelectableFn = NonNullable<
  DataGridProProps<EditorialAssetSearchResultItem>['isRowSelectable']
>;
type HandleRowSelectModelFn = NonNullable<
  DataGridProProps<EditorialAssetSearchResultItem>['onRowSelectionModelChange']
>;
type GetRowIdFn = NonNullable<
  DataGridProProps<EditorialAssetSearchResultItem>['getRowId']
>;
type ColVisModelChangeFn = NonNullable<DataGridProProps['onColumnVisibilityModelChange']>;
type ColWidthChangeFn = NonNullable<DataGridProProps['onColumnWidthChange']>;
type ColOrderChangeFn = NonNullable<DataGridProProps['onColumnOrderChange']>;
type PinnedColChangeFn = NonNullable<DataGridProProps['onPinnedColumnsChange']>;
type RowsScrollEndFn = NonNullable<DataGridProProps['onRowsScrollEnd']>;
type SortModelChangeFn = NonNullable<DataGridProProps['onSortModelChange']>;
type GetRowHeightFn = NonNullable<DataGridProProps['getRowHeight']>;
type GridSlotsType = Partial<DataGridProProps['slots']>;
type GridSlotPropsType = Partial<DataGridProProps['slotProps']>;
type GridLocaleTextType = NonNullable<DataGridProProps['localeText']>;

/**
 * --------------------------------------------------------
 * Search Modal Datagrid - Editorial
 * - Handles grid state, columns, styles, and selection.
 * --------------------------------------------------------
 */
export const SearchModalDatagridEditorial = memo(() => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { editorialConfig } = useCosmosEditorial();
  const { sessionConfig } = useCosmos();
  const {
    resource,
    filters,
    setFilters,
    view,
    dataGridApiRef,
    selectedIds,
    setSelections,
    checkItemSelectDisabled,
    selectionMode,
    getRecordId,
    sortDirConfig,
  } = useSearchModalContext();
  const { data, isFetching } = useListContext<EditorialAssetSearchResultItem>();
  const { fetchNextPage, isFetchingNextPage, hasNextPage } =
    useInfinitePaginationContext();

  // Debounce the fetchNextPage function, help prevent any double fetches
  const debouncedFetchNextPage = useDebouncedCallback(fetchNextPage, 200);

  // Sort keys from config
  const sortKey = sortDirConfig?.sortKey;
  const dirKey = sortDirConfig?.dirKey;

  /**
   * A small debounce seems to mitigate a visual bug:
   * there is an edge case with mui datagrid where in
   * single select mode, if very rapidly selecting each row
   * via the checkboxes, it can display as "checked" visually as
   * multiple, but the actual react state is correct, being only
   * the last selected row. Seems something to do with the datagrid row
   * virtualization state and the checkbox ripple animations...
   */
  const [dbSelectedIds] = useDebouncedValue(selectedIds, 30);
  const dbSelectedIdsModel = useMemo<GridRowSelectionModel>(() => {
    return {
      type: 'include',
      ids: new Set(dbSelectedIds),
    };
  }, [dbSelectedIds]);

  // React state for grid initial state and rows data
  const [gridInitialState, setGridInitialState] = useState<ReactGridInitState>(null);
  const [dataGridRows, setDataGridRows] = useState<ReactRowsInitState>(undefined);

  // Get styles and row height info
  const memoizedDatagridStyleInfo = useMemo(
    () => getDatagridStylesInfo({ theme, view, resource }),
    [theme, view, resource],
  );

  const { sxStyles: dgStyles, rowHeight: cellHeight } = memoizedDatagridStyleInfo;

  // Memoized column definitions
  const colDefs = useMemo(() => {
    const cols = getGridColumnDefs({
      t: t,
      view: view,
      resource: resource,
      sessionConfig: sessionConfig,
      editorialConfig: editorialConfig,
    }) as EditorialColDefs;
    return cols;
  }, [t, resource, view, sessionConfig, editorialConfig]);

  // Get base grid initial state
  const baseGridInitialState = useMemo<NonNullable<ReactGridInitState>>(
    () =>
      getGridInitialState(resource) ?? {
        preferencePanel: {
          open: false,
        },
      },
    [resource],
  );

  /**
   * Helper for effect to initialize grid state from local storage
   */
  const initializeGridStateSnapshot = useCallback(() => {
    try {
      const gridStorageKey = getGridStorageLSKey(resource, view);
      const gridStateInStorage = localStorage.getItem(gridStorageKey);
      let finalGridState: ReactGridInitState = null;
      // Restore from storage if exists, or build from base + filters.
      if (gridStateInStorage) {
        const parsedState = JSON.parse(gridStateInStorage);
        // Format coldefs based on the stored state
        const formattedColDefs = buildColDefsViaStateExport({
          gridState: parsedState,
          colDefs: colDefs,
        });
        // Restore augmented state
        finalGridState = augmentGridState({
          gridState: parsedState,
          colDefs: formattedColDefs,
          filters: filters,
          baseGridInitialState: baseGridInitialState,
          sortKey: sortKey,
          dirKey: dirKey,
        });
      } else {
        // No stored state, rebuild from base + filters
        finalGridState = augmentGridState({
          gridState: baseGridInitialState,
          colDefs: colDefs,
          filters: filters,
          baseGridInitialState: baseGridInitialState,
          sortKey: sortKey,
          dirKey: dirKey,
        });
      }

      // Check result sort model and remove sort/dir if needed
      const sortModels = finalGridState?.sorting?.sortModel || [];
      const sortModelEmpty = sortModels.length === 0;
      const filtersHasSortOrDir =
        Boolean(filters?.[sortKey]) || Boolean(filters?.[dirKey]);
      if (sortModelEmpty && filtersHasSortOrDir) {
        setFilters((prev) => {
          const updated = { ...prev };
          delete updated[sortKey];
          delete updated[dirKey];
          return updated;
        });
      }

      // Set the result state for the grid
      setGridInitialState(finalGridState);
    } catch (error) {
      console.error('[initializeGridStateSnapshot] Error:', error);
    }
  }, [
    baseGridInitialState,
    colDefs,
    dirKey,
    filters,
    resource,
    setFilters,
    sortKey,
    view,
  ]);

  // Setup initial grid state once...
  useLayoutEffect(() => {
    if (gridInitialState !== null) {
      return;
    }
    initializeGridStateSnapshot();
  }, [gridInitialState, initializeGridStateSnapshot]);

  // Save grid state to local storage helper
  const saveGridState = useCallback(
    (details: GridCallbackDetails<any>) => {
      saveGridStateLocalStorage({
        details: details,
        resource: resource,
        view: view,
        filters: filters,
        baseGridInitialState: baseGridInitialState,
        colDefs: colDefs,
        sortKey: sortKey,
        dirKey: dirKey,
      });
    },
    [baseGridInitialState, colDefs, dirKey, filters, resource, sortKey, view],
  );

  // Get row id helper
  const handleGetRowId: GetRowIdFn = useCallback(
    (row) => {
      const rowId = getRecordId(row, resource);
      return rowId;
    },
    [getRecordId, resource],
  );

  // Handle columns model changes
  const handleColVisModelChange: ColVisModelChangeFn = useCallback(
    (model, details) => {
      saveGridState(details);
    },
    [saveGridState],
  );

  const handleColWidthChange: ColWidthChangeFn = useCallback(
    (params, event, details) => {
      saveGridState(details);
    },
    [saveGridState],
  );

  const handleColOrderChange: ColOrderChangeFn = useCallback(
    (params, event, details) => {
      saveGridState(details);
    },
    [saveGridState],
  );

  const handlePinnedColsChange: PinnedColChangeFn = useCallback(
    (pinnedColumns, details) => {
      saveGridState(details);
    },
    [saveGridState],
  );

  // Handle infinite scroll to fetch next page
  const handleOnRowsScrollEnd = useCallback<RowsScrollEndFn>(async () => {
    if (!hasNextPage || isFetchingNextPage || isFetching) {
      return;
    }
    debouncedFetchNextPage();
  }, [debouncedFetchNextPage, hasNextPage, isFetchingNextPage, isFetching]);

  // Load in rows as data fetches
  useLayoutEffect(() => {
    if (!Array.isArray(data)) {
      return;
    }
    if (!isFetching && !isFetchingNextPage) {
      setDataGridRows(data);
    }
  }, [data, isFetching, isFetchingNextPage]);

  // Handle sort model changes
  const handleSortModelChange: SortModelChangeFn = useCallback(
    (model) => {
      // MUI Datagrid allows for some multisort functions
      // but we only use single hence the [0] access
      const sortField = model?.[0]?.field ?? '';
      const sortOrder = model?.[0]?.sort?.toUpperCase() ?? '';
      // Update sort and dir in filters state
      setFilters((prev) => {
        if (sortField && sortOrder) {
          return {
            ...prev,
            [sortKey]: sortField,
            [dirKey]: sortOrder,
          };
        } else {
          // Remove dynamic sort + dir keys from filters
          // Use bracket syntax to remove the actual dynamic keys
          const next = { ...prev };
          delete next[sortKey];
          delete next[dirKey];
          return next;
        }
      });
    },
    [dirKey, setFilters, sortKey],
  );

  // Return row cells height - precalculated via style helper
  const handleGetRowHeight: GetRowHeightFn = useCallback(
    (params) => {
      return cellHeight;
    },
    [cellHeight],
  );

  /**
   * Get row class names for styling
   * zebra striping and disabled styles
   */
  const handleGetRowClassName: GetRowClassnameFn = useCallback(
    (params) => {
      const { row, indexRelativeToCurrentPage } = params;

      const isDisabled = checkSelectDisabled({
        resource: resource,
        record: row,
        checkItemSelectDisabled: checkItemSelectDisabled,
      });

      // Classes with zebra striping and disabled state
      const classes = classNames({
        even: indexRelativeToCurrentPage % 2 === 0,
        odd: indexRelativeToCurrentPage % 2 !== 0,
        disabled: isDisabled,
      });

      return classes;
    },
    [checkItemSelectDisabled, resource],
  );

  /**
   * Check whether row is selectable
   */
  const handleIsRowSelectable: GetRowSelectableFn = useCallback(
    (params) => {
      const { row } = params;
      const isDisabled = checkSelectDisabled({
        resource: resource,
        record: row,
        checkItemSelectDisabled: checkItemSelectDisabled,
      });
      return !isDisabled;
    },
    [checkItemSelectDisabled, resource],
  );

  /**
   * Note: MUI DataGrid invokes this function on-mount (and if selection changes via grid api)
   * and we cannot control exactly when it will be called, they do this so you can
   * 'restore selections' on mount/reload, which is not currently applicable to our usage.
   * So even if nothing is selected, MUI it will invoke with empty[] on mount...
   * We should guard the setState call to prevent infinite loops
   * recommended to conlog and watch for rerender loops if modifying this fn.
   * console.debug('[render] PerfNow:', performance.now());
   */
  const handleRowSelectModel: HandleRowSelectModelFn = useCallback(
    (model, details) => {
      // MUI Datagrid model does not give us full record, only selected ids so we map ids back to records.

      const modelIds =
        model.type === 'exclude'
          ? (dataGridRows
              ?.map((item) => getRecordId(item, resource))
              .filter((id): id is string => Boolean(id) && !model.ids.has(id)) ?? [])
          : Array.from(model.ids);

      // guard if selection mode is not defined
      if (!selectionMode) {
        return;
      }

      // no update if both are empty
      const bothEmpty = modelIds.length === 0 && selectedIds?.length === 0;
      if (bothEmpty) {
        return;
      }

      // no update if arrays are equal
      const unchanged = isDeepEqual(modelIds, selectedIds);
      if (unchanged) {
        return;
      }

      /**
       * Handle single selection mode:
       * Only keep the most recently selected.
       * If swapped to single mode and there are multiple,
       * previous selections, only keep most recent selection.
       */
      if (selectionMode === 'single') {
        setSelections((prevSelections) => {
          const lastIndex = modelIds.length - 1;
          const mostRecentId = modelIds[lastIndex];
          const idsToProcess = [mostRecentId];
          const newSelected: EditorialAssetSearchResultItem[] = [];

          // Find the record in the data by its id
          for (const id of idsToProcess) {
            if (!id) {
              continue;
            }
            const rec = dataGridRows?.find((item) => {
              const itemId = getRecordId(item, resource);
              if (!itemId) {
                return false;
              }
              return itemId === id;
            });
            if (rec) {
              newSelected.push(rec);
            }
          }
          return newSelected;
        });
      }

      /**
       * Handle multiple selection mode:
       * Keep prior selections that aren't in current dataGridRows
       * This can occur for example:
       * -- select some items, change filters, scroll/load more, then select more...
       * If the new data does not have the prior selected items, they would
       * be removed by MUI datagrid unless we preserve them here.
       */
      if (selectionMode === 'multiple') {
        setSelections((prevSelections) => {
          const idsToProcess = modelIds;

          // Preserve existing selections (may not be in current dataGridRows data)
          const currentRowIds = new Set(
            dataGridRows
              ?.map((item) => {
                return getRecordId(item, resource);
              })
              .filter((id) => {
                return Boolean(id);
              }) ?? [],
          );

          // Get items to preserve (not in current rows)
          const preservedItems = prevSelections.filter((item) => {
            const itemId = getRecordId(item, resource);
            if (!itemId) {
              return false;
            }
            const inCurrentRows = currentRowIds.has(itemId);
            return !inCurrentRows;
          });

          const newSelected: EditorialAssetSearchResultItem[] = [...preservedItems];

          for (const id of idsToProcess) {
            if (!id) {
              continue;
            }

            // Find the record in the data by its id
            const rec = dataGridRows?.find((item) => {
              const itemId = getRecordId(item, resource);
              if (!itemId) {
                return false;
              }
              return itemId === id;
            });

            if (rec) {
              newSelected.push(rec);
            }
          }

          return newSelected;
        });
      }
    },
    [dataGridRows, getRecordId, resource, selectedIds, selectionMode, setSelections],
  );

  // Translation strings / constants
  const strNoResults = t('blox.general.no_results_found');
  const strLoading = t('blox.general.loading');
  // Disable multi select if not in multiple mode
  const disableMultiSelect = selectionMode !== 'multiple';

  // note on gridslots, cannot do something eg:
  // cell: (muiCellProps) =>( <SearchGridCell {...muiCellProps} customProps={...}/> )
  // as datagrid will complain about fwdRef, though this doesnt seem to cause
  // any real issues, we will need to specify the function (not jsx) in the gridSlots
  // and then pass our custom props down via slotProps, which is a bit annoying.
  // Or, those props need to be set to a context that is accessible in custom slots.
  // Note MUI baseCheckbox is used for ALL checkboxes within the datagrid
  // row, header, column selector, etc... more notes inside the component
  const gridSlots: GridSlotsType = useMemo(() => {
    return {
      cell: CustomGridCell,
      row: SearchModalEditorialGridRow,
      baseCheckbox: CustomGridCheckbox,
      loadingOverlay: LinearProgress as GridSlots['loadingOverlay'],
    } as GridSlotsType;
  }, []);

  // pass or override props to gridslots
  const gridSlotProps: GridSlotPropsType = useMemo(() => {
    return {
      row: { customProps: {} } as Partial<SearchModalEditorialGridRowProps>,
      cell: { customProps: {} } as Partial<CustomGridCellProps>,
      baseCheckbox: { customProps: {} } as Partial<CustomGridCheckboxProps>,
      columnsManagement: defaultColumnsManagementSlotProps,
    };
  }, []);

  const gridLocaleText: GridLocaleTextType = useMemo(() => {
    return {
      noRowsLabel: strNoResults,
    };
  }, [strNoResults]);

  if (!dataGridRows || !colDefs || !gridInitialState || !dataGridApiRef) {
    return (
      <SkeletonBox
        text={strLoading}
        width="100%"
        height="100%"
      />
    );
  }

  return (
    <DataGridPro
      apiRef={dataGridApiRef}
      columns={colDefs}
      rows={dataGridRows}
      localeText={gridLocaleText}
      slots={gridSlots}
      slotProps={gridSlotProps}
      initialState={gridInitialState}
      loading={isFetchingNextPage}
      rowSelectionModel={dbSelectedIdsModel}
      checkboxSelection={true}
      disableMultipleRowSelection={disableMultiSelect}
      disableRowSelectionExcludeModel={true}
      keepNonExistentRowsSelected={true} // keep selections across filter changes
      onRowSelectionModelChange={handleRowSelectModel} // keep selections across filter changes
      rowSelection={true}
      disableRowSelectionOnClick={true} // handled in row component
      getRowId={handleGetRowId}
      density="compact"
      sortingMode="server"
      filterMode="server"
      rowsLoadingMode="client"
      scrollEndThreshold={300}
      getRowHeight={handleGetRowHeight}
      getRowClassName={handleGetRowClassName}
      isRowSelectable={handleIsRowSelectable}
      onRowsScrollEnd={handleOnRowsScrollEnd}
      onSortModelChange={handleSortModelChange}
      onColumnVisibilityModelChange={handleColVisModelChange}
      onColumnWidthChange={handleColWidthChange}
      onColumnOrderChange={handleColOrderChange}
      onPinnedColumnsChange={handlePinnedColsChange}
      disableColumnFilter={true}
      disableVirtualization={false}
      disableDensitySelector={true}
      disableMultipleColumnsSorting={true}
      disableMultipleColumnsFiltering={true}
      disableColumnSelector={false}
      disableColumnPinning={false}
      disableColumnSorting={false}
      disableColumnMenu={false}
      hideFooter={true}
      sx={dgStyles}
    />
  );
});
SearchModalDatagridEditorial.displayName = 'SearchModalDatagridEditorial';

/**
 * --------------------------------------------------------
 * Custom Datagrid Slots - Cell, Row, Checkbox
 * Namespaced props to avoid collision with mui props.
 * --------------------------------------------------------
 */

interface SearchModalEditorialGridRowProps extends GridRowProps {
  customProps?: {};
}

export const SearchModalEditorialGridRow = memo(
  forwardRef<HTMLDivElement, SearchModalEditorialGridRowProps>(
    ({ customProps, ...muiRowProps }, ref) => {
      const apiRef = useGridApiContext();

      const {
        resource,
        setSelections,
        selections,
        checkItemSelectDisabled,
        handleSelectItem,
        clearSelections,
        miniMenuConfig,
      } = useSearchModalContext();

      // State for right click menu
      const [menuEvent, setMenuEvent] = useState<TMenuState>(null);
      // Anchor elem for button menu (col actions / useSearchRowMenu)
      const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

      const reactKey = `search-modal-row-${muiRowProps?.rowId}`;
      const rowData = apiRef.current.getRow<EditorialAssetSearchResultItem>(
        muiRowProps?.rowId,
      );

      // Determine if selection/styling is disabled
      const isSelectDisabled = useMemo(() => {
        const isDisabled = checkSelectDisabled({
          resource: resource,
          record: rowData,
          checkItemSelectDisabled: checkItemSelectDisabled,
        });
        return isDisabled;
      }, [checkItemSelectDisabled, rowData, resource]);

      // Handle context menu open and checks
      const handleContextMenu: TSetMenuEvent = useCallback(
        (event) => {
          event?.preventDefault();
          event?.stopPropagation();
          if (isSelectDisabled) {
            return;
          }
          // Open menu at position
          setMenuEvent(event);
        },
        [isSelectDisabled, setMenuEvent],
      );

      const handleMiniMenuClose = useCallback(() => {
        setMenuEvent(null);
        setAnchorEl(null);
      }, []);

      const openMenuAtAnchor = useCallback((el: HTMLElement) => {
        setAnchorEl(el);
      }, []);

      // Actions column triple dot menu needs this
      const rowMenuCtx = useMemo<SearchRowMenuContextValue>(
        () => ({ openMenuAtAnchor }),
        [openMenuAtAnchor],
      );

      const handleClearSelections = useCallback(() => {
        clearSelections();
      }, [clearSelections]);

      /**
       * Handle row click - toggle row item selection...
       * Delegate to context selection handler
       */
      const handleRowClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          handleSelectItem({ item: rowData });
        },
        [handleSelectItem, rowData],
      );

      return (
        <Fragment key={reactKey}>
          <SearchRowMenuContext.Provider value={rowMenuCtx}>
            <Box
              data-testid="search-modal-row-wrapper"
              onContextMenu={handleContextMenu}
              onClick={handleRowClick}
            >
              <GridRow
                ref={ref}
                {...muiRowProps}
              />
            </Box>
          </SearchRowMenuContext.Provider>
          <EditorialMiniMenu
            menuEvent={menuEvent}
            setMenuEvent={setMenuEvent}
            handleMiniMenuClose={handleMiniMenuClose}
            clearSelectedItems={handleClearSelections}
            setSelectedItems={setSelections}
            selectedItems={selections}
            raResource={resource}
            gridApiRef={apiRef}
            contextMenuItem={rowData}
            isContextItemDisabled={isSelectDisabled}
            menuConfig={miniMenuConfig}
            anchorEl={anchorEl}
          />
        </Fragment>
      );
    },
  ),
);
SearchModalEditorialGridRow.displayName = 'SearchModalEditorialGridRow';

/**
 * Cell allows props via slotProps, or can use context.
 */
interface CustomGridCellProps extends GridCellProps {
  customProps?: {
    [key: string]: any;
  };
}

export const CustomGridCell = memo(
  forwardRef<HTMLDivElement, SearchGridCellProps>(
    ({ customProps, ...muiCellProps }, ref) => {
      return (
        <GridCell
          ref={ref}
          {...muiCellProps}
        />
      );
    },
  ),
);
CustomGridCell.displayName = 'CustomGridCell';

/**
 * Note: checkbox is used for all checkboxes in the
 * datagrid when using baseCheckbox gridslots so it
 * will be applied to row, header, col menu, etc.
 *
 * This component is to stop/catch propagation of
 * the click event via keeping the row wrapped.
 */

type MuiDgBaseChekboxProps = GridSlotProps['baseCheckbox'];
interface CustomGridCheckboxProps extends MuiDgBaseChekboxProps {
  customProps?: {
    [key: string]: any;
  };
}

export const CustomGridCheckbox = memo(
  forwardRef<HTMLButtonElement, CustomGridCheckboxProps>(
    ({ customProps, label, disabled, onClick, fullWidth, ...restMuiProps }, ref) => {
      const handleCheckboxClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
          // Prevent click bubbling (eg: stop navigation)
          event.stopPropagation();
          // Call the mui onClick handler if provided
          if (onClick) {
            onClick?.(event);
          }
        },
        [onClick],
      );

      // Clean up some mui styles on form ctrl wrapper.
      const formCtrlSx: FormControlLabelProps['sx'] = useMemo(() => {
        return {
          margin: 0,
          width: fullWidth ? '100%' : undefined,
        };
      }, [fullWidth]);

      // Note: MUI v8 passes label prop for the col mgmt panel.
      // Pass checkbox thru FormCtrlLabel so text is visible.
      return (
        <FormControlLabel
          label={label}
          disabled={disabled}
          sx={formCtrlSx}
          control={
            <Checkbox
              ref={ref}
              disabled={disabled}
              {...restMuiProps}
              onClick={handleCheckboxClick}
            />
          }
        />
      );
    },
  ),
);
CustomGridCheckbox.displayName = 'CustomGridCheckbox';
